import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { COUNTRIES } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const money = (v, c = "USD") => `${c} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const TYPES = [
  ["working_capital", "Working capital"], ["equipment", "Equipment / machinery"],
  ["term_loan", "Term loan"], ["invoice_finance", "Invoice finance"],
  ["trade_credit", "Trade credit"], ["vehicle", "Vehicle finance"],
  ["overdraft", "Overdraft"], ["microloan", "Microloan"],
];
const FAQS = [
  ["What is the true cost of credit?", "Total interest plus arrangement and processing fees across the whole term. It is the only number worth comparing — a lower advertised rate with a 2% fee often costs more."],
  ["Does a lower instalment mean a cheaper loan?", "No. Stretching the term lowers the monthly payment and raises the total interest. Compare the true cost, then check the instalment is affordable."],
  ["Is the scheme eligibility answer binding?", "No, it is indicative. It checks turnover, years trading, MSME status and ownership against each published scheme; the scheme officer makes the decision."],
];

export default function FinanceChecker() {
  const [form, setForm] = useState({
    country: "US", finance_type: "working_capital", amount: 25000, months: 24,
    annual_turnover: 250000, years_trading: 3, women_owned: false, msme: true,
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useSeo({
    title: "Free business finance & government scheme checker | Unity & Utility",
    description: "Compare working capital, equipment and term finance on true cost of credit, and see which government schemes your business qualifies for. Free, no sign-in.",
    path: "/tools/finance-checker",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        breadcrumbs([["Home", "/"], ["Finance checker", "/tools/finance-checker"]]),
        { "@type": "FAQPage", mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
      ],
    },
  });

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public-site/finance-check", {
        country: form.country, finance_type: form.finance_type,
        amount: Number(form.amount) || 0, months: Number(form.months) || 12,
        annual_turnover: Number(form.annual_turnover) || 0,
        years_trading: Number(form.years_trading) || 0,
        women_owned: form.women_owned, msme: form.msme,
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="finance-checker-page">
        <Crumbs items={[["Home", "/"], ["Finance checker", null]]} />
        <p className="overline text-[#002FA7]">Free tool</p>
        <h1 className="font-display mt-3 text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl">
          Finance &amp; government scheme checker
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-600">
          Six questions about the business. You get the instalment, the true cost of credit across the
          lenders that fit, and the government schemes you qualify for — plus the ones you are one
          answer short of.
        </p>

        <div className="mt-8 grid gap-3 border border-border bg-white p-5 sm:grid-cols-2">
          <div>
            <Label>Country</Label>
            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
              <SelectTrigger data-testid="finance-country-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>What for</Label>
            <Select value={form.finance_type} onValueChange={(v) => setForm({ ...form, finance_type: v })}>
              <SelectTrigger data-testid="finance-type-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>How much</Label><Input data-testid="finance-amount-input" type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-none border-slate-300" /></div>
          <div><Label>Over how many months</Label><Input data-testid="finance-months-input" type="number" min="1" value={form.months} onChange={(e) => setForm({ ...form, months: e.target.value })} className="rounded-none border-slate-300" /></div>
          <div><Label>Annual turnover</Label><Input data-testid="finance-turnover-input" type="number" min="0" value={form.annual_turnover} onChange={(e) => setForm({ ...form, annual_turnover: e.target.value })} className="rounded-none border-slate-300" /></div>
          <div><Label>Years trading</Label><Input data-testid="finance-years-input" type="number" min="0" value={form.years_trading} onChange={(e) => setForm({ ...form, years_trading: e.target.value })} className="rounded-none border-slate-300" /></div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" data-testid="finance-msme-checkbox" checked={form.msme} onChange={(e) => setForm({ ...form, msme: e.target.checked })} />
            MSME / small business
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" data-testid="finance-women-checkbox" checked={form.women_owned} onChange={(e) => setForm({ ...form, women_owned: e.target.checked })} />
            Women-owned
          </label>
          <div className="sm:col-span-2">
            <Button data-testid="finance-check-button" onClick={run} disabled={busy} className="w-full sm:w-auto">
              <Bank size={16} className="mr-1.5" /> {busy ? "Checking…" : "Check my options"}
            </Button>
          </div>
        </div>

        {result && (
          <div className="mt-6 space-y-6" data-testid="finance-results">
            <div>
              <h2 className="text-base font-semibold text-[#0F172A] md:text-lg">Lenders that fit</h2>
              {result.offers.length === 0 ? (
                <p data-testid="finance-no-offers" className="mt-2 border border-border bg-white p-5 text-sm text-slate-600">
                  No published product matches that amount and country yet. Try a different amount, or
                  sign in to put the request to lenders directly.
                </p>
              ) : (
                <div className="mt-3 space-y-2">
                  {result.offers.map((o, i) => (
                    <div key={`${o.product}-${i}`} data-testid={`finance-offer-${i}`} className={`border bg-white p-4 ${i === 0 ? "border-[#002FA7]" : "border-border"}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold text-[#0F172A]">
                            {o.product}
                            {i === 0 && <span className="ml-2 border border-[#A7E3CE] bg-[#E6F7F1] px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-[#0B7A5B]">Cheapest overall</span>}
                          </div>
                          <div className="text-xs text-slate-500">{o.lender_name} · {o.lender_kind} · {o.interest_rate}% a year</div>
                        </div>
                        <div className="text-right">
                          <div className="font-display text-lg font-bold text-[#0F172A]" data-testid={`finance-instalment-${i}`}>{money(o.instalment, o.currency)}</div>
                          <div className="text-[0.7rem] uppercase tracking-wider text-slate-500">a month</div>
                        </div>
                      </div>
                      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                        {[["Fees", o.fee], ["Interest", o.total_interest], ["True cost", o.true_cost], ["Total repayable", o.total_repayable]].map(([l, v]) => (
                          <div key={l}>
                            <dt className="overline text-[0.6rem] text-slate-400">{l}</dt>
                            <dd className="text-slate-700">{money(v, o.currency)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className="text-base font-semibold text-[#0F172A] md:text-lg">
                Government schemes ({result.schemes_qualify.length} of {result.schemes_checked} fit)
              </h2>
              <div className="mt-3 space-y-2" data-testid="finance-schemes">
                {result.schemes_qualify.map((s, i) => (
                  <div key={`${s.name}-${i}`} data-testid={`finance-scheme-${i}`} className="border border-[#A7E3CE] bg-[#F3FBF7] p-4">
                    <div className="text-sm font-semibold text-[#0F172A]">{s.name}</div>
                    <div className="text-xs text-slate-500">{s.authority} · {s.country} · {s.subsidy_pct}% support up to {money(s.ceiling_amount, s.currency)}</div>
                    {s.summary && <p className="mt-1.5 text-sm text-slate-600">{s.summary}</p>}
                  </div>
                ))}
                {result.schemes_nearly.map((s, i) => (
                  <div key={`near-${s.name}-${i}`} data-testid={`finance-scheme-near-${i}`} className="border border-border bg-white p-4">
                    <div className="text-sm font-semibold text-[#0F172A]">{s.name}</div>
                    <div className="text-xs text-[#B3241B]">One answer short: {s.why_not.join(", ")}</div>
                  </div>
                ))}
                {result.schemes_qualify.length === 0 && result.schemes_nearly.length === 0 && (
                  <p data-testid="finance-no-schemes" className="border border-border bg-white p-5 text-sm text-slate-600">
                    No published scheme matches that profile yet.
                  </p>
                )}
              </div>
            </div>

            <p className="text-xs text-slate-500">{result.note}</p>
            <Link to="/register" data-testid="finance-cta-register" className="inline-flex items-center gap-2 bg-[#002FA7] px-5 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#00248A]">
              Apply properly <ArrowRight size={15} />
            </Link>
          </div>
        )}

        <h2 className="mt-12 text-base font-semibold text-[#0F172A] md:text-lg">Questions people ask</h2>
        <dl className="mt-3 space-y-4" data-testid="finance-faqs">
          {FAQS.map(([q, a]) => (
            <div key={q} className="border border-border bg-white p-4">
              <dt className="text-sm font-semibold text-[#0F172A]">{q}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-slate-600">{a}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-10 border-t border-border pt-5">
          <p className="text-xs text-slate-500">Share this checker</p>
          <div className="mt-2"><ShareBar url={`${window.location.origin}/tools/finance-checker`} text="Free business finance and scheme checker" testid="finance-share" /></div>
        </div>

        <p className="mt-8 text-sm text-slate-600">
          Related: <Link to="/solutions/finance" className="font-semibold text-[#002FA7] hover:underline">finance in the platform</Link>
          {" · "}<Link to="/solutions/government-schemes" className="font-semibold text-[#002FA7] hover:underline">government schemes</Link>
        </p>
      </div>
    </PublicShell>
  );
}
